import { classNames } from '@fullstory/util/fmt';
import React, { MouseEventHandler, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { identifyComponent } from '../../util';
import { ButtonBase } from '../ButtonBase';
import { Icon } from '../Icon';
import { Text } from '../Text';
import css from './index.module.scss';

type FilterChipSize = 'x-small' | 'small' | 'medium' | 'large';
const iconSize = {
  'x-small': 16,
  small: 16,
  medium: 16,
  large: 20,
} as const;

const textSize = {
  'x-small': 'bodySmall',
  small: 'bodyMedium',
  medium: 'bodyMedium',
  large: 'bodyLarge',
} as const;

export interface FilterChipProps {
  /**
   * For element identity and testing purposes. Default to 'filterChip'
   */
  testId?: string;
  /**
   * An optional label to reveal contextual information of the contents
   */
  label?: string;
  /**
   * Size of the FilterChip
   */
  size?: FilterChipSize;
  /**
   * Fires when the button is clicked or activated via a keypress. If onClick
   * is present, the chip will change appearance on hover, focus, and click
   */
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  /**
   * Fires when the remove icon is clicked or activated via a keypress. If onRemove
   * is present the chip will show a remove icon
   */
  onRemove?: () => void;
  /**
   * Content to be rendered on the chip.
   */
  children: ReactNode;
}

export const FilterChip = ({
  testId = 'filterChip',
  label,
  size = 'x-small',
  onClick,
  onRemove,
  children,
}: FilterChipProps) => {
  const intl = useIntl();
  const clickable = Boolean(onClick);
  const removable = Boolean(onRemove);

  const labelAppearance = textSize[size];
  const textAppearance = `${textSize[size]}Bold` as const;

  return (
    <div
      data-component="FilterChip"
      className={classNames(css.container, {
        [css.removable]: removable,
      })}
      {...identifyComponent('filter-chip')}
    >
      <ButtonBase
        testId={testId}
        className={classNames(css.button, css.filterButton, css[size])}
        disabled={!clickable} // disabling if not clickabled per ARIA standards.
        aria-label={typeof children === 'string' ? children : undefined}
        onClick={onClick}
      >
        <FormattedMessage
          defaultMessage="<g>{group}:</g> <l>{label}</l>"
          description="The 'group' is a label that adds context to the 'label' string separated by a colon. The colon needs to be translated."
          id="fQ1Izw"
          values={{
            g(chunks) {
              if (!label) return null;
              return (
                <Text className={css.label} appearance={labelAppearance} tag="span" color="inherit">
                  {chunks}
                </Text>
              );
            },
            group: label,
            l(chunks) {
              return (
                <Text appearance={textAppearance} tag="span" color="inherit">
                  {chunks}
                </Text>
              );
            },
            label: children,
          }}
        />
      </ButtonBase>
      {onRemove && (
        <ButtonBase
          testId={`${testId}-remove`}
          className={classNames(css.button, css.removeButton, css[size])}
          aria-label={intl.formatMessage({ defaultMessage: 'remove', id: '1LmW+v' })}
          onClick={onRemove}
        >
          <Icon name="close" size={iconSize[size]} />
        </ButtonBase>
      )}
    </div>
  );
};
